import { create } from 'zustand';

export interface UploadedFileData {
  id: string;
  name: string;
  rowCount: number;
  columns: string[];
  data: Record<string, unknown>[];
}

interface UploadedFileState {
  /** Per-node files: nodeId → UploadedFileData */
  files: Record<string, UploadedFileData>;
  /** 原始 Excel 文件（仅 load_excel）：运行工作流时上传此文件以保留样式 */
  originalExcelFiles: Record<string, File>;
  /** AI 助手上传的多文件列表（支持多表工作流） */
  aiDrawerFiles: UploadedFileData[];
  isUploading: boolean;

  /**
   * Backward-compat derived value.
   * 优先为 aiDrawerFiles[0]，否则为 files 中第一个。Legacy 组件用此表示「当前文件」。
   */
  uploadedFile: UploadedFileData | null;

  // Per-node API
  setFileForNode: (nodeId: string, file: UploadedFileData | null) => void;
  setOriginalExcelFile: (nodeId: string, file: File | null) => void;
  getOriginalExcelFile: (nodeId: string) => File | null;
  clearFileForNode: (nodeId: string) => void;
  clearAllFiles: () => void;
  setIsUploading: (v: boolean) => void;

  // AI 助手多文件
  setAiDrawerFiles: (files: UploadedFileData[]) => void;
  addAiDrawerFile: (file: UploadedFileData) => void;
  removeAiDrawerFile: (id: string) => void;
  clearAiDrawerFiles: () => void;

  // Legacy aliases kept for components that don't know about nodeId
  setUploadedFile: (file: UploadedFileData | null) => void;
  clearFile: () => void;
}

function firstFile(files: Record<string, UploadedFileData>): UploadedFileData | null {
  const values = Object.values(files);
  return values.length > 0 ? values[0] : null;
}

function derivedUploadedFile(
  aiDrawerFiles: UploadedFileData[],
  files: Record<string, UploadedFileData>
): UploadedFileData | null {
  if (aiDrawerFiles.length > 0) return aiDrawerFiles[0];
  return firstFile(files);
}

export const useUploadedFileStore = create<UploadedFileState>()((set, get) => ({
  files: {},
  originalExcelFiles: {},
  aiDrawerFiles: [],
  isUploading: false,
  uploadedFile: null,

  setFileForNode: (nodeId, file) =>
    set((state) => {
      const newFiles =
        file === null
          ? Object.fromEntries(Object.entries(state.files).filter(([k]) => k !== nodeId))
          : { ...state.files, [nodeId]: file };
      return {
        files: newFiles,
        uploadedFile: derivedUploadedFile(state.aiDrawerFiles, newFiles),
      };
    }),

  setOriginalExcelFile: (nodeId, file) =>
    set((state) => {
      if (file === null) {
        const { [nodeId]: _, ...rest } = state.originalExcelFiles;
        return { originalExcelFiles: rest };
      }
      return {
        originalExcelFiles: { ...state.originalExcelFiles, [nodeId]: file },
      };
    }),

  getOriginalExcelFile: (nodeId) => get().originalExcelFiles[nodeId] ?? null,

  clearFileForNode: (nodeId) =>
    set((state) => {
      const newFiles = Object.fromEntries(
        Object.entries(state.files).filter(([k]) => k !== nodeId)
      );
      const { [nodeId]: _, ...restExcel } = state.originalExcelFiles;
      return {
        files: newFiles,
        uploadedFile: derivedUploadedFile(state.aiDrawerFiles, newFiles),
        originalExcelFiles: restExcel,
      };
    }),

  clearAllFiles: () =>
    set({
      files: {},
      originalExcelFiles: {},
      aiDrawerFiles: [],
      uploadedFile: null,
    }),

  setAiDrawerFiles: (files) =>
    set((state) => ({
      aiDrawerFiles: files,
      uploadedFile: derivedUploadedFile(files, state.files),
    })),

  addAiDrawerFile: (file) =>
    set((state) => {
      const next = [...state.aiDrawerFiles, file];
      return {
        aiDrawerFiles: next,
        uploadedFile: derivedUploadedFile(next, state.files),
      };
    }),

  removeAiDrawerFile: (id) =>
    set((state) => {
      const next = state.aiDrawerFiles.filter((f) => f.id !== id);
      return {
        aiDrawerFiles: next,
        uploadedFile: derivedUploadedFile(next, state.files),
      };
    }),

  clearAiDrawerFiles: () =>
    set((state) => ({
      aiDrawerFiles: [],
      uploadedFile: firstFile(state.files),
    })),

  setIsUploading: (v) => set({ isUploading: v }),

  // Legacy: 单文件时同时写入 __global__ 与 aiDrawerFiles，便于兼容
  setUploadedFile: (file) =>
    set((state) => {
      const list = file == null ? [] : [file];
      const newFiles =
        file === null
          ? Object.fromEntries(Object.entries(state.files).filter(([k]) => k !== '__global__'))
          : { ...state.files, __global__: file };
      return {
        files: newFiles,
        aiDrawerFiles: list,
        uploadedFile: derivedUploadedFile(list, newFiles),
      };
    }),

  clearFile: () =>
    set((state) => {
      const newFiles = Object.fromEntries(
        Object.entries(state.files).filter(([k]) => k !== '__global__')
      );
      return {
        files: newFiles,
        aiDrawerFiles: [],
        uploadedFile: firstFile(newFiles),
      };
    }),
}));
